/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IMenuItemData, INavigationMenuViewProps } from '@msdyn365-commerce-modules/navigation-menu';
import { Navbar, NavbarToggler, Nav, NavItem, NavLink, Collapse, UncontrolledDropdown, DropdownToggle, DropdownMenu } from 'reactstrap';
import * as React from 'react';
import DropdownItem from 'reactstrap/lib/DropdownItem';

interface IMenuItemDataClass extends IMenuItemData {
  className?: string;
}

interface NavState {
  isOpen: boolean;
}

/**
 *
 * NavigationMenuView component
 * @extends {React.PureComponent<INavigationMenuViewProps>}
 */
export class NavigationMenuView extends React.PureComponent<INavigationMenuViewProps, NavState> {
  private currentLevel: number = 0;
  constructor(props: INavigationMenuViewProps) {
    super(props);
    this.state = {
      isOpen: false
    };
  }

  public render(): JSX.Element | null {

    return (
        <Navbar expand="md" light>
          <NavbarToggler onClick={() => this.setState({isOpen: !this.state.isOpen})} className="ml-auto" />
          <Collapse isOpen={this.state.isOpen} navbar>
            <Nav className="ml-auto" navbar>
              {this._renderDisplay()}
            </Nav>
          </Collapse>
        </Navbar>
    );
  }
  private _renderDisplay(): JSX.Element[] {
    const { menuItemData } = this.props;
    const menuItemList: JSX.Element[] = [];
    menuItemData.forEach((item: IMenuItemData, index: number) => {
      menuItemList.push(
        <React.Fragment key={index}>
          {this._createMenuItemList(item)}
        </React.Fragment>
      );
    });
    return menuItemList;
  }
  private _createMenuItemList(menuItemData: IMenuItemData): JSX.Element | null {
    this.currentLevel = 1;
    if (menuItemData && menuItemData.subMenu && menuItemData.subMenu.length > 0) {
        return (
          <UncontrolledDropdown nav inNavbar>
            {this._renderButtonMenuItem(menuItemData)}
            <DropdownMenu right>
              {this._renderSubMenu(menuItemData.subMenu)}
            </DropdownMenu>
          </UncontrolledDropdown>
        );
    } else if (menuItemData && menuItemData.linkText && menuItemData.linkURL && menuItemData.linkURL.length > 0) {
      return (this._renderLinkMenuItem(menuItemData));
    } else if (menuItemData && menuItemData.linkText && !menuItemData.linkURL) {
      return (this._renderTextMenuItem(menuItemData));
    }
    return null;
  }
  private _renderSubMenu(subMenus?: IMenuItemDataClass[]): JSX.Element | null {
    if (!subMenus || subMenus.length === 0) {
      return null;
    }
    this.currentLevel++;
    const menuOptions = subMenus && subMenus.map((option: IMenuItemDataClass, idx: number) => {
      const hasOptions = (option.subMenu && option.subMenu.length > 0);
      let menuItem: JSX.Element | null;
      if (hasOptions) {
        menuItem = this._renderButtonMenuItem(option, idx);
      } else {
        menuItem = (option.linkURL
          ? this._renderLinkMenuItem(option)
          : this._renderTextMenuItem(option)
        );
      }
      return (
        <React.Fragment key={option.id}>
          {menuItem}
        </React.Fragment>
      );
    });
    return (this._renderMenu(menuOptions));
  }
  private _renderButtonMenuItem(option: IMenuItemData, index?: number): JSX.Element | null {
    return (
      <DropdownToggle caret nav key={index}>
        {option.linkText}
        <i className="ml-1 fas fa-chevron-down"></i>
      </DropdownToggle>
    );
  }
  private _renderLinkMenuItem(option: IMenuItemData): JSX.Element | null {
    if (this.currentLevel === 1) {
      return (
        <NavItem>
          <NavLink href={option.linkURL}>
            {option.linkText}
          </NavLink>
        </NavItem>
      );
    }
    return (
      <DropdownItem tag="a" href={option.linkURL}>
        {option.linkText}
      </DropdownItem>
    );
  }
  private _renderTextMenuItem(option: IMenuItemData, index?: number): JSX.Element | null {
    return (
      <span className="navbar-text">{option.linkText}</span>
    );
  }
  private _renderMenu(menuOptions: JSX.Element[]): JSX.Element | null {
    return (
      <React.Fragment>
        {menuOptions}
      </React.Fragment>
    );
  }
}
export default NavigationMenuView;